import ToastActions from 'actions/ToastActions';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox';
import Columns2 from 'components/Columns2';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Row from 'components/Row';
import InputValueConstants from 'constants/InputValueConstants';
import { UpdateUserByIDInput, UserNameChangeBanInput } from 'globalTypes';
import assertions from 'lib/assertions';
import React, { useEffect, useState } from 'react';

type EditNameMember = {
  uuid: string;
  firstName: string;
  lastName: string;
  userInteractionPermissions: {
    canBanNameChanges?: boolean;
    canChangeName?: boolean;
  };
  isNameChangeBanned?: boolean;
};

export type EditNameProperties = {
  member: EditNameMember;
  organizationUuid?: string;
  groupUuid?: string;
  refetch: (eventName?: string) => Promise<any>;
  updateUserByID?: (args: { variables: { input: UpdateUserByIDInput } }) => Promise<any>;
  setUserNameChangeBan?: (args: { variables: { input: UserNameChangeBanInput } }) => Promise<any>;
  unsetUserNameChangeBan?: (args: { variables: { input: UserNameChangeBanInput } }) => Promise<any>;
};

export const EditNameForm = ({
  member,
  organizationUuid,
  groupUuid,
  refetch,
  updateUserByID = async () => {},
  setUserNameChangeBan = async () => {},
  unsetUserNameChangeBan = async () => {},
}: EditNameProperties): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    banNameChanges: false,
  });

  const handleSetUserNameChangeBan = async () => {
    if (!member.userInteractionPermissions.canBanNameChanges) {
      return;
    }

    const response = await setUserNameChangeBan({
      variables: {
        input: {
          user_identifier: member.uuid,
        },
      },
    });

    if (response?.data?.setUserNameChangeBan?.error) {
      throw new Error(response?.data?.setUserNameChangeBan?.error);
    }
  };

  const handleUnsetUserNameChangeBan = async () => {
    if (!member.userInteractionPermissions.canBanNameChanges) {
      return;
    }

    const response = await unsetUserNameChangeBan({
      variables: {
        input: {
          user_identifier: member.uuid,
        },
      },
    });

    if (response?.data?.setUserNameChangeBan?.error) {
      throw new Error(response?.data?.unsetUserNameChangeBan?.error);
    }
  };

  const handleBanNameChanges = async (forceCheck: boolean): Promise<void> => {
    if (!member.isNameChangeBanned || forceCheck) {
      if (state.banNameChanges) {
        await handleSetUserNameChangeBan();
      }
    }

    if (member.isNameChangeBanned && !state.banNameChanges) {
      await handleUnsetUserNameChangeBan();
    }
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();

    setLoading(true);

    let handleBanNameChangesCheck = false;

    try {
      if (member.firstName !== state.firstName || member.lastName !== state.lastName) {
        if (member.isNameChangeBanned) {
          handleBanNameChangesCheck = true;
          await handleUnsetUserNameChangeBan();
        }

        const response = await updateUserByID({
          variables: {
            input: {
              uuid: member.uuid,
              first_name: state.firstName,
              last_name: state.lastName,
              group_uuid: groupUuid,
              organization_uuid: organizationUuid,
            },
          },
        });

        if (response?.data?.updateUserByID?.error) {
          throw new Error(response?.data?.updateUserByID?.error);
        }
      }

      await handleBanNameChanges(handleBanNameChangesCheck);

      await refetch('user_profile.name.updated');
      ToastActions.showSuccessToast('Changes saved');

      // eslint-disable-next-line no-unused-expressions
      const closeButton = document.getElementsByClassName('Dialog-closeButton')[0] as any;
      closeButton?.click();
    } catch (error) {
      const message = 'Unable to save changes. Please try again or contact support.';
      ToastActions.showErrorToast(message);
    }

    setLoading(false);
  };

  useEffect(() => {
    setState({
      firstName: member.firstName,
      lastName: member.lastName,
      displayName: '',
      banNameChanges: Boolean(member.isNameChangeBanned),
    });
  }, [member]);

  const enableSave =
    state.firstName !== member.firstName ||
    state.lastName !== member.lastName ||
    state.banNameChanges !== Boolean(member.isNameChangeBanned);

  return (
    <form className="EditNameForm" onSubmit={onSubmit}>
      <Columns2 gutter="small">
        <FormElement
          label="First name"
          validate={(e) => {
            assertions.lengthLesserThanOrEqualTo(
              e.target.value,
              InputValueConstants.FIRST_NAME_LENGTH
            );
          }}
          onValidChange={(e) => setState({ ...state, firstName: e.target.value })}
          onInvalidChange={(e) => setState({ ...state, firstName: e.target.value })}
          element={Input}
          elementProps={{
            value: state.firstName,
            'data-test': 'first-name-input',
          }}
        />

        <FormElement
          label="Last name"
          validate={(e) => {
            assertions.lengthLesserThanOrEqualTo(
              e.target.value,
              InputValueConstants.LAST_NAME_LENGTH
            );
          }}
          onValidChange={(e) => setState({ ...state, lastName: e.target.value })}
          onInvalidChange={(e) => setState({ ...state, lastName: e.target.value })}
          element={Input}
          elementProps={{
            value: state.lastName,
            'data-test': 'last-name-input',
          }}
        />
      </Columns2>

      {member.userInteractionPermissions.canBanNameChanges && (
        <Row size="small">
          <Checkbox
            onChange={(e) => setState({ ...state, banNameChanges: e.target.checked })}
            label="Ban this person from changing their name."
            checked={state.banNameChanges}
            data-test="ban-name-checkbox"
          />
        </Row>
      )}

      <Button
        full
        type="submit"
        onClick={onSubmit}
        loading={loading}
        data-test="button-submit"
        disabled={!enableSave}
      >
        Save
      </Button>
    </form>
  );
};
